import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Target, Palette, Package, Award, BookOpen } from "lucide-react";
import Ornament, { SectionOverline } from "../components/Ornament";
import SmartImage from "../components/SmartImage";
import { api } from "../lib/config";
import useSEO from "../hooks/useSEO";

const BENEFITS = [
  { icon: Target, title: "Cahier des charges respecté", text: "Chaque projet suit rigoureusement le brief défini ensemble." },
  { icon: Palette, title: "Cohérence de marque", text: "Supports alignés avec votre identité visuelle." },
  { icon: Package, title: "Petites & moyennes séries", text: "Production flexible selon vos volumes." },
  { icon: Award, title: "Finition haut de gamme", text: "Matières nobles, rendu soigné, durable." },
];

const ProPage = () => {
  useSEO({
    title: "Espace Professionnels",
    description: "Plaques entreprise, trophées personnalisés, cadeaux d'entreprise gravés, porte-clés Airbnb. Passion Verre accompagne les pros avec des supports sur mesure (petite et moyenne série).",
  });
  const [bestsellers, setBestsellers] = useState([]);
  const [proRealizations, setProRealizations] = useState([]);

  useEffect(() => {
    (async () => {
      const [bs, p] = await Promise.all([api.get("/best-sellers"), api.get("/pro-realizations")]);
      setBestsellers(bs.data);
      setProRealizations(p.data);
    })();
  }, []);

  return (
    <div data-testid="pro-page">
      {/* HERO */}
      <section className="pt-32 pb-20 bg-[#171717]">
        <div className="max-w-5xl mx-auto px-6">
          <Ornament />
          <div className="text-center">
            <SectionOverline className="mt-6">Espace Professionnels</SectionOverline>
            <h1 className="font-serif text-5xl sm:text-6xl text-white mt-4 mb-8 leading-tight">Votre communication visuelle, sur mesure</h1>
            <p className="text-base sm:text-lg text-[#DDD4B2]/80 max-w-3xl mx-auto leading-relaxed">
              Vous cherchez à renforcer la communication visuelle de votre entreprise avec des supports personnalisés, cohérents avec votre image de marque ? Passion Verre conçoit et réalise vos projets sur mesure, en respectant scrupuleusement le cahier des charges défini ensemble lors de nos premiers échanges. Notre atelier travaille en petite et moyenne série, avec un catalogue complet en matériaux, couleurs, quantités et personnalisation.
            </p>
          </div>
        </div>
      </section>

      {/* BENEFITS */}
      <section className="py-20 bg-[#DDD4B2] text-[#171717]">
        <div className="max-w-6xl mx-auto px-6 grid grid-cols-2 md:grid-cols-4 gap-8">
          {BENEFITS.map((b, i) => (
            <motion.div
              key={b.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              className="text-center"
            >
              <div className="inline-flex items-center justify-center w-14 h-14 border border-[#9A8640] text-[#9A8640] mb-4">
                <b.icon size={22} />
              </div>
              <h3 className="font-serif text-xl mb-2">{b.title}</h3>
              <p className="text-sm text-[#171717]/75 leading-relaxed">{b.text}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* BEST SELLERS */}
      <section className="py-24 sm:py-32 bg-[#171717]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <SectionOverline>Best-sellers</SectionOverline>
            <h2 className="font-serif text-4xl sm:text-5xl text-[#DDD4B2] mt-3">Les incontournables</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {bestsellers.map((b, i) => (
              <motion.div
                key={b.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.1 }}
                className="group relative overflow-hidden aspect-[5/4]"
                data-testid={`bestseller-${b.id}`}
              >
                <img src={b.image_url} alt={b.title} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#171717]/95 via-[#171717]/40 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-8">
                  <h3 className="font-serif text-3xl text-white mb-3">{b.title}</h3>
                  <p className="text-sm text-[#DDD4B2]/85 leading-relaxed">{b.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* PRO REALIZATIONS */}
      {proRealizations.length > 0 && (
        <section className="py-24 bg-[#0f0f0f]">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-14">
              <SectionOverline>Réalisations professionnelles</SectionOverline>
              <h2 className="font-serif text-4xl sm:text-5xl text-[#DDD4B2] mt-3">Ils nous ont fait confiance</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {proRealizations.map((p, i) => (
                <motion.div
                  key={p.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: (i % 3) * 0.1 }}
                  className="group bg-[#171717] border border-[#9A8640]/25 hover:border-[#9A8640]/60 transition-all overflow-hidden flex flex-col"
                  data-testid={`pro-realization-${p.id}`}
                >
                  <div className="aspect-[4/3] overflow-hidden relative">
                    <SmartImage src={p.image_url} alt={p.title} width={700} eager={i < 2} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                    {p.blog_slug && (
                      <span className="absolute top-3 right-3 bg-[#9A8640] text-[#171717] text-[10px] uppercase tracking-[0.15em] px-2 py-1 rounded-full inline-flex items-center gap-1">
                        <BookOpen size={10} /> Article
                      </span>
                    )}
                  </div>
                  <div className="p-6 flex-1 flex flex-col">
                    <h3 className="font-serif text-2xl text-white mb-3">{p.title}</h3>
                    {p.description && <p className="text-sm text-[#DDD4B2]/75 leading-relaxed flex-1">{p.description}</p>}
                    {p.blog_slug && (
                      <Link to={`/blog/${p.blog_slug}`} className="mt-5 inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-[#9A8640] hover:gap-3 transition-all" data-testid={`pro-realization-blog-${p.id}`}>
                        Lire l'article <ArrowRight size={14} />
                      </Link>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-32 bg-[#DDD4B2] text-[#171717]">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <Ornament className="mb-6" />
          <h2 className="font-serif text-4xl sm:text-5xl mb-6">Parlons de votre projet</h2>
          <p className="text-base sm:text-lg text-[#171717]/80 mb-10">Un brief, une demande de devis, une simple idée ? Contactez-nous, nous revenons vers vous sous 48h.</p>
          <Link to="/contact" className="inline-flex items-center gap-3 bg-[#171717] text-[#DDD4B2] px-10 py-4 text-sm uppercase tracking-[0.18em] hover:bg-[#9A8640] hover:text-[#171717] transition-all" data-testid="pro-cta-contact">
            Demander un devis <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default ProPage;
